<div class="seg-detail-page" *ngIf="!loading && record">
  <header class="seg-detail-header">
    <button mat-button type="button" (click)="back()"><mat-icon>arrow_back</mat-icon> All inspections</button>
    <div class="seg-detail-title">
      <h1>{{ segmentTitle() }}</h1>
      <p>{{ record.project_name }} · Status: <strong>{{ record.status }}</strong></p>
    </div>
    <button mat-stroked-button color="primary" type="button" (click)="openMap()" *ngIf="record.map_link">
      <mat-icon>map</mat-icon> Segment on map
    </button>
  </header>

  <mat-horizontal-stepper [selectedIndex]="stepIndex()" linear="false" class="workflow-stepper">
    <mat-step label="Supervisor — document verification">
      <section class="step-card" *ngIf="canDeclareReady()">
        <h2>Declare segment ready for inspection</h2>
        <p>Verify document completeness using the checklist, then set the planned inspection date.</p>

        <div class="checklist-block">
          <h3>Document completeness checklist</h3>
          <div class="checklist-item" *ngFor="let item of documentChecklist">
            <mat-checkbox [checked]="item.checked" (change)="item.checked = $event.checked">
              {{ item.label }}
              <span class="mandatory" *ngIf="item.mandatory">*</span>
            </mat-checkbox>
            <input class="remarks-input" [(ngModel)]="item.remarks" placeholder="Remarks (optional)" />
          </div>
        </div>

        <form [formGroup]="declareForm" class="step-form">
          <mat-form-field appearance="outline" class="full">
            <mat-label>Planned inspection date</mat-label>
            <input matInput type="date" formControlName="planned_inspection_date" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Work status summary (for engineer)</mat-label>
            <textarea matInput rows="3" formControlName="work_status_summary"></textarea>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Supervisor notes</mat-label>
            <textarea matInput rows="2" formControlName="notes"></textarea>
          </mat-form-field>
          <button mat-raised-button color="primary" type="button" (click)="declareReady()" [disabled]="saving">
            Declare ready on SigmaT ConFlux
          </button>
        </form>
      </section>
      <p *ngIf="!canDeclareReady()" class="step-done-msg">Supervisor verification completed.</p>
    </mat-step>

    <mat-step label="L1 — nominate engineer">
      <section class="step-card" *ngIf="canNominate()">
        <h2>Nominate inspection engineer</h2>
        <p>Engineer receives email and in-app notification with segment location link and work status document.</p>

        <div class="work-status-doc" *ngIf="record.work_status_summary">
          <h3>Work status (standard format)</h3>
          <pre>{{ record.work_status_summary }}</pre>
        </div>

        <form [formGroup]="nominateForm" class="step-form">
          <mat-form-field appearance="outline" class="full">
            <mat-label>Inspection engineer</mat-label>
            <mat-select formControlName="inspection_engineer_id">
              <mat-option *ngFor="let s of staff" [value]="s.id">{{ s.full_name }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Inspection date (override optional)</mat-label>
            <input matInput type="date" formControlName="planned_inspection_date" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Notes to engineer</mat-label>
            <textarea matInput rows="2" formControlName="notes"></textarea>
          </mat-form-field>
          <button mat-raised-button color="primary" type="button" (click)="nominateEngineer()" [disabled]="saving">
            Notify engineer
          </button>
        </form>
      </section>
      <p *ngIf="record.inspection_engineer_name && !canNominate()" class="step-done-msg">
        Assigned to {{ record.inspection_engineer_name }} · Planned {{ record.planned_inspection_date | date:'mediumDate' }}
      </p>
    </mat-step>

    <mat-step label="Engineer — site inspection">
      <section class="step-card" *ngIf="canComplete()">
        <h2>Complete geo-tagged inspection</h2>
        <p>Visit site, capture location, complete QA and material consumption verification.</p>

        <div class="geo-block">
          <button mat-stroked-button type="button" (click)="captureGeo()" [disabled]="geoLoading">
            <mat-icon>my_location</mat-icon>
            {{ geoLoading ? 'Capturing...' : 'Capture site GPS' }}
          </button>
          <span *ngIf="record.inspection_geo_lat" class="geo-coords">
            {{ record.inspection_geo_lat | number:'1.5-5' }}, {{ record.inspection_geo_lng | number:'1.5-5' }}
          </span>
          <span class="geo-error" *ngIf="geoError">{{ geoError }}</span>
        </div>

        <div class="checklist-block">
          <h3>QA process checklist</h3>
          <div class="checklist-item" *ngFor="let item of qaChecklist">
            <mat-checkbox [checked]="item.checked" (change)="item.checked = $event.checked">
              {{ item.label }}<span class="mandatory" *ngIf="item.mandatory">*</span>
            </mat-checkbox>
            <input class="remarks-input" [(ngModel)]="item.remarks" placeholder="Remarks" />
          </div>
        </div>

        <div class="checklist-block">
          <h3>Material consumption approval</h3>
          <div class="checklist-item" *ngFor="let item of materialChecklist">
            <mat-checkbox [checked]="item.checked" (change)="item.checked = $event.checked">
              {{ item.label }}<span class="mandatory" *ngIf="item.mandatory">*</span>
            </mat-checkbox>
            <input class="remarks-input" [(ngModel)]="item.remarks" placeholder="Remarks" />
          </div>
        </div>

        <form [formGroup]="completeForm" class="step-form">
          <mat-form-field appearance="outline" class="full">
            <mat-label>Findings summary</mat-label>
            <textarea matInput rows="3" formControlName="findings_summary"></textarea>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Compliance result</mat-label>
            <mat-select formControlName="compliance_result">
              <mat-option value="pass">Pass</mat-option>
              <mat-option value="conditional">Conditional pass</mat-option>
              <mat-option value="fail">Fail</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-checkbox formControlName="material_consumption_approved">Material consumption approved</mat-checkbox>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Engineer notes</mat-label>
            <textarea matInput rows="2" formControlName="notes"></textarea>
          </mat-form-field>
          <button mat-raised-button color="primary" type="button" (click)="completeInspection()" [disabled]="saving">
            Submit inspection
          </button>
        </form>
      </section>

      <section class="step-card result-card" *ngIf="isFinished()">
        <h2>Inspection result</h2>
        <p><strong>Compliance:</strong> {{ record.compliance_result }}</p>
        <p><strong>Material approved:</strong> {{ record.material_consumption_approved ? 'Yes' : 'No' }}</p>
        <p *ngIf="record.findings_summary"><strong>Findings:</strong> {{ record.findings_summary }}</p>
        <p *ngIf="record.actual_inspection_date"><strong>Completed:</strong> {{ record.actual_inspection_date | date:'medium' }}</p>
        <p *ngIf="record.inspection_geo_lat">
          <strong>Geo:</strong> {{ record.inspection_geo_lat | number:'1.5-5' }}, {{ record.inspection_geo_lng | number:'1.5-5' }}
        </p>
      </section>
    </mat-step>
  </mat-horizontal-stepper>

  <section class="history-card" *ngIf="record.workflow_history?.length">
    <h3>Workflow history</h3>
    <ul>
      <li *ngFor="let h of record.workflow_history">
        <strong>{{ h.action }}</strong> — {{ h.actor_name || 'System' }} · {{ h.at | date:'medium' }}
        <span *ngIf="h.notes"> · {{ h.notes }}</span>
      </li>
    </ul>
  </section>
</div>

<div *ngIf="loading" class="loading-wrap">Loading...</div>
